import React from "react";
import { useNavigate } from "react-router-dom";
import Card from "../../../components/ui/Card.jsx";

const CATS = [
  { code: "btl", label: "BTL / Complex BTL", desc: "HMO · MUFB · Short Term Let" },
  { code: "if", label: "Invoice Finance", desc: "XLSX bulk upload" },
  { code: "af", label: "AF", desc: "Asset Finance" },
  { code: "bridging", label: "Bridging", desc: "Short-term funding" },
];

const isEnabled = (code) => code === "btl" || code === "if";

const uploadPath = (code) => {
  if (!isEnabled(code)) return null;
  if (code === "if") return "/lender/upload/if/csv";
  if (code === "btl") return "/lender/upload/btl/manual";
  return null;
};

export default function ProductSelectModal({ onClose }) {
  const navigate = useNavigate();
  const [active, setActive] = React.useState(0);
  const itemRefs = React.useRef([]);

  React.useEffect(() => itemRefs.current[0]?.focus(), []);
  React.useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const go = (code) => {
    const path = uploadPath(code);
    if (path) navigate(path);
  };

  const onGridKeyDown = (e) => {
    const max = CATS.length - 1;
    if (["ArrowRight", "ArrowDown"].includes(e.key)) {
      e.preventDefault(); const next = Math.min(active + 1, max); setActive(next); itemRefs.current[next]?.focus();
    } else if (["ArrowLeft", "ArrowUp"].includes(e.key)) {
      e.preventDefault(); const prev = Math.max(active - 1, 0); setActive(prev); itemRefs.current[prev]?.focus();
    } else if (e.key === "Enter") {
      e.preventDefault(); const cat = CATS[active]; if (isEnabled(cat.code)) go(cat.code);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" role="dialog" aria-modal="true">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-[2px]" onClick={onClose} />
      <div className="relative z-10 w-[min(640px,96vw)] max-h-[92vh] overflow-auto">
        <Card className="p-6">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-xs uppercase tracking-wide text-gray-500">Upload Products</div>
              <h3 className="text-2xl font-extrabold mt-1">Choose a product</h3>
              <p className="text-gray-600 mt-1 text-sm">
                Pick a category to continue.
              </p>
            </div>
            <button aria-label="Close" className="px-3 py-1.5 rounded-lg border hover:bg-gray-50" onClick={onClose}>
              ×
            </button>
          </div>

          <div
            className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-3 p-0.5"
            role="listbox"
            aria-label="Product categories"
            onKeyDown={onGridKeyDown}
          >
            {CATS.map((c, idx) => {
              const enabled = isEnabled(c.code);
              return (
                <button
                  key={c.code}
                  ref={(el) => (itemRefs.current[idx] = el)}
                  role="option"
                  aria-selected={active === idx}
                  aria-disabled={!enabled}
                  disabled={!enabled}
                  onClick={() => go(c.code)}
                  onFocus={() => setActive(idx)}
                  className={`group relative text-left p-4 border rounded-2xl bg-white transition ${enabled
                      ? "hover:bg-sky-50 hover:border-sky-300 hover:shadow-sm focus-visible:outline-none focus-visible:border-sky-400 focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-sky-400"
                      : "opacity-60 cursor-not-allowed"
                    }`}
                >
                  <div className="flex items-center gap-3">
                    <div
                      className={`shrink-0 h-11 w-11 rounded-xl ${enabled
                          ? "bg-gray-100 text-gray-700 group-hover:bg-sky-100 group-hover:text-sky-700"
                          : "bg-gray-100 text-gray-400"
                        } font-extrabold grid place-items-center transition`}
                    >
                      {c.label.replace(/[^A-Z]/g, "").slice(0, 3) || c.label.slice(0, 2).toUpperCase()}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className={`font-semibold ${enabled ? "text-gray-900 group-hover:text-sky-800" : "text-gray-500"}`}>
                        {c.label}
                      </div>
                      <div className="text-xs text-gray-500 mt-0.5 truncate">{c.desc}</div>
                    </div>
                  </div>
                  {!enabled ? (
                    <span className="absolute top-2 right-2 text-[10px] px-2 py-1 rounded-full border bg-gray-50 text-gray-600">
                      Coming soon
                    </span>
                  ) : null}
                </button>
              );
            })}
          </div>
        </Card>
      </div>
    </div>
  );
}
